import React, { useEffect, useRef, useState } from 'react';
import { subjects } from '../data/mock';
import { Badge } from './ui/badge';
import { Calculator, FlaskConical, Languages, BookText, BookMarked, Globe, Monitor, Atom } from 'lucide-react';

const iconMap = {
  Calculator,
  FlaskConical,
  Languages,
  BookText,
  BookMarked,
  Globe,
  Monitor,
  Atom
};

const colors = [
  { bg: 'bg-teal-50', icon: 'text-teal-600', hover: 'hover:bg-teal-50/80', border: 'hover:border-teal-200' },
  { bg: 'bg-amber-50', icon: 'text-amber-600', hover: 'hover:bg-amber-50/80', border: 'hover:border-amber-200' },
  { bg: 'bg-sky-50', icon: 'text-sky-600', hover: 'hover:bg-sky-50/80', border: 'hover:border-sky-200' },
  { bg: 'bg-rose-50', icon: 'text-rose-600', hover: 'hover:bg-rose-50/80', border: 'hover:border-rose-200' },
  { bg: 'bg-violet-50', icon: 'text-violet-600', hover: 'hover:bg-violet-50/80', border: 'hover:border-violet-200' },
  { bg: 'bg-emerald-50', icon: 'text-emerald-600', hover: 'hover:bg-emerald-50/80', border: 'hover:border-emerald-200' },
  { bg: 'bg-orange-50', icon: 'text-orange-600', hover: 'hover:bg-orange-50/80', border: 'hover:border-orange-200' },
  { bg: 'bg-cyan-50', icon: 'text-cyan-600', hover: 'hover:bg-cyan-50/80', border: 'hover:border-cyan-200' }
];

export const Subjects = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="subjects" ref={ref} className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 px-4 py-1.5 text-sm font-medium mb-6">
            Subjects
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            All Subjects,{' '}
            <span className="text-teal-600">One Destination</span>
          </h2>
          <p className="text-lg text-slate-500">
            Complete subject coverage from KG to Class X.
          </p>
        </div>

        {/* Subjects grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {subjects.map((subject, index) => {
            const Icon = iconMap[subject.icon];
            const color = colors[index % colors.length];
            return (
              <div
                key={subject.id}
                className={`group relative bg-white rounded-2xl p-6 border border-slate-100 ${color.border} ${color.hover} hover:shadow-lg transition-all duration-400 hover:-translate-y-0.5 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className={`w-12 h-12 rounded-xl ${color.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-400`}>
                  {Icon && <Icon className={`w-6 h-6 ${color.icon}`} />}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{subject.name}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{subject.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
